// components/ErrorBoundary.tsx
import { HomeOutlined, ReloadOutlined, RobotOutlined } from '@ant-design/icons';
import { Button, Space, Typography } from 'antd';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useNavigate } from 'react-router';

const { Text } = Typography;

export const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const navigate = useNavigate();
  const isDev = import.meta.env.MODE === 'development';

  const goHome = () => {
    navigate('/');
    resetErrorBoundary();
  };

  return (
    <div
      className={`bg-gray-100 flex flex-col items-center justify-center min-h-screen p-12 ${isDev ? 'p-12' : ''}`}
    >
      <div className="text-center">
        {/* <h1 className="text-7xl font-extrabold text-orange-500">404</h1> */}
        <RobotOutlined className="text-7xl font-extrabold text-orange-500" />
        <p className="text-2xl font-medium text-gray-800 mt-4">
          Something Went Wrong
        </p>
        <p className="text-gray-600 mt-2">
          We're sorry for the inconvenience. Please try refreshing the page.
        </p>

        <Space>
          <Button
            size="large"
            variant="filled"
            type="primary"
            className="mt-6 font-semibold"
            icon={<HomeOutlined />}
            onClick={goHome}
          >
            Go Back Home
          </Button>
          <Button
            size="large"
            variant="filled"
            className="mt-6 font-semibold"
            icon={<ReloadOutlined />}
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </Space>
      </div>

      {isDev && (
        <div className="mt-8 max-w-[800px] mx-auto py-[40px] px-[24px]">
          <Text type="secondary">Error Details:</Text>
          <pre className="bg-gray-100 p-3 rounded overflow-x-auto">
            <code>{error.message}</code>
            <br />
            <code>{error.stack}</code>
          </pre>
        </div>
      )}
    </div>
  );
};

export const AppErrorBoundary = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => window.location.reload()}
      onError={(error, info) => {
        console.error('Error caught by error boundary:', error, info);
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
